import { COLUMN_ADDITION_MULTI_PAGE } from "../config/gridLinesConfig";
import { getCellDifferences } from "./tableCalculations";
import { Box, Typography } from "@mui/material";
import React from "react";

// ============================================================================
// CONSTANTS
// ============================================================================

/** UI Colors */
const COLORS = {
  NEW_COLUMN: "#d4edda", // Green for new columns
  MODIFIED_COLUMN: "#fff3cd", // Yellow for modified columns
  TO_BE_SPLIT: "#cfe2ff", // Blue for columns to be split
  EMPTY_CELL: "#f8f9fa", // Light gray for empty cells
  REMOVED_CONTENT: "#f8d7da", // Light red for removed content
  ADDED_CONTENT: "#d4edda", // Light green for added content
  MODIFIED_CONTENT: "#cfe2ff", // Light blue for modified content
  HOVER_ADDED: "#b8e6b8", // Darker green for hover
  HOVER_REMOVED: "#ffb8b8", // Darker red for hover
  HOVER_MODIFIED: "#a8d8f0", // Darker blue for hover
  HOVER_DEFAULT: "#f0f0f0", // Default hover color
  HEADER_BG: "#f5f5f5", // Header background
  ROW_BORDER: "#eee", // Row border color
};

/** UI Text */
const UI_TEXT = {
  NEW: "NEW",
  SPLIT: "SPLIT",
  TO_BE_SPLIT: "TO BE SPLIT",
  EMPTY: "(empty)",
  NO_TABLE_DATA: "No table data available",
  NO_CHANGES: "No changes detected in this table",
  NO_ROWS: "No rows with changes in this view",
};

/** UI Dimensions */
const DIMENSIONS = {
  MAX_HEIGHT: "400px",
  TABLE_HEIGHT: "300px",
  MIN_COL_WIDTH: "200px",
  MAX_COL_WIDTH: "300px",
  CELL_PADDING: "8px 12px",
  FONT_SIZE_NORMAL: "0.875rem",
  FONT_SIZE_TABLE: "0.75rem",
  FONT_SIZE_SMALL: "0.8rem",
  FONT_SIZE_TINY: "0.6rem",
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Determine which columns to show in the table preview
 */
const determineColumnsToShow = (
  splitColumnIndex,
  isAfter,
  isColumnStructuralChange,
  differences
) => {
  if (splitColumnIndex !== undefined) {
    return isAfter
      ? [splitColumnIndex, splitColumnIndex + 1]
      : [splitColumnIndex];
  }

  // For changes without splitColumnIndex, show changed columns
  return Array.from(differences.changedColumns).sort((a, b) => a - b);
};

/**
 * Determine which rows to show in the table preview
 */
const determineRowsToShow = (
  isRowStructuralChange,
  isAfter,
  pendingGridLineChanges,
  differences
) => {
  if (isRowStructuralChange) {
    const splitRowIndex = pendingGridLineChanges?.splitRowIndex;

    if (splitRowIndex === undefined) return [];

    if (isAfter) {
      // Show both original and new row
      const originalRowIndex = splitRowIndex - 1;
      const newRowIndex = splitRowIndex;
      return originalRowIndex >= 0
        ? [originalRowIndex, newRowIndex]
        : [newRowIndex];
    } else {
      // Show only original row
      const originalRowIndex = splitRowIndex - 1;
      return originalRowIndex >= 0 ? [originalRowIndex] : [];
    }
  } else {
    // Show rows with changes
    const rowsWithChanges = new Set();
    if (
      differences.changes &&
      typeof differences.changes.forEach === "function"
    ) {
      differences.changes.forEach((cellKey) => {
        if (cellKey && typeof cellKey === "string" && cellKey.includes("_")) {
          const [rowStr] = cellKey.split("_");
          if (rowStr && !isNaN(parseInt(rowStr))) {
            rowsWithChanges.add(parseInt(rowStr));
          }
        }
      });
    }
    return Array.from(rowsWithChanges).sort((a, b) => a - b);
  }
};

/**
 * Calculate background color for a cell based on changes and column splits
 */
const calculateCellBackgroundColor = (
  cellContent,
  splitColumnIndex,
  colIndex,
  isAfter,
  hasChange,
  changeInfo
) => {
  // Default background
  let backgroundColor = cellContent ? "inherit" : COLORS.EMPTY_CELL;

  // For column splits, force color changes for affected columns
  if (
    splitColumnIndex !== undefined &&
    (colIndex === splitColumnIndex || colIndex === splitColumnIndex + 1) &&
    cellContent.trim()
  ) {
    if (colIndex === splitColumnIndex) {
      backgroundColor = isAfter ? COLORS.TO_BE_SPLIT : COLORS.MODIFIED_COLUMN;
    } else if (colIndex === splitColumnIndex + 1) {
      backgroundColor = isAfter ? COLORS.NEW_COLUMN : COLORS.EMPTY_CELL;
    }
  } else if (hasChange && changeInfo && changeInfo.type) {
    if (changeInfo.type === "added") {
      backgroundColor = isAfter ? COLORS.ADDED_CONTENT : COLORS.EMPTY_CELL;
    } else if (changeInfo.type === "removed") {
      backgroundColor = isAfter ? COLORS.EMPTY_CELL : COLORS.REMOVED_CONTENT;
    } else if (changeInfo.type === "modified") {
      backgroundColor = isAfter
        ? COLORS.MODIFIED_CONTENT
        : COLORS.MODIFIED_COLUMN;
    }
  }

  return backgroundColor;
};

/**
 * Calculate hover background color for a cell
 */
const calculateHoverColor = (hasChange, changeInfo) => {
  if (hasChange && changeInfo && changeInfo.type) {
    switch (changeInfo.type) {
      case "added":
        return COLORS.HOVER_ADDED;
      case "removed":
        return COLORS.HOVER_REMOVED;
      default:
        return COLORS.HOVER_MODIFIED;
    }
  }
  return COLORS.HOVER_DEFAULT;
};

/**
 * Renders a new column preview specifically for column splitting
 */
export const renderNewColumnPreview = (
  tableData,
  title,
  isAfter = false,
  pendingGridLineChanges,
  borderColor
) => {
  // Use the stored splitColumnIndex from the split result if available
  const splitColumnIndex =
    pendingGridLineChanges?.newTableData?.splitColumnIndex;

  if (!tableData || !tableData.headers || !tableData.rows) {
    return (
      <Typography variant={"body2"} color={"text.secondary"}>
        {UI_TEXT.NO_TABLE_DATA}
      </Typography>
    );
  }

  // For new column splits, determine which columns to show
  let columnsToShow = [];

  if (splitColumnIndex !== undefined) {
    if (isAfter) {
      columnsToShow = [splitColumnIndex, splitColumnIndex + 1];
    } else {
      columnsToShow = [splitColumnIndex];
    }
  }

  return (
    <Box>
      <Typography variant={"subtitle1"} sx={{ mb: 2, fontWeight: "bold" }}>
        {title}
      </Typography>

      <Box
        sx={{
          border: `1px solid ${borderColor}`,
          borderRadius: 1,
          overflow: "hidden",
          backgroundColor: "white",
        }}
      >
        {/* Headers */}
        <Box
          sx={{
            display: "flex",
            backgroundColor: COLORS.HEADER_BG,
            borderBottom: `1px solid ${borderColor}`,
          }}
        >
          {columnsToShow.map((colIndex, displayIndex) => {
            // New column detection: for splits, the new column is at splitColumnIndex + 1
            const isNewColumn =
              isAfter &&
              splitColumnIndex !== undefined &&
              colIndex === splitColumnIndex + 1;
            const isOriginalInSplit =
              isAfter &&
              splitColumnIndex !== undefined &&
              colIndex === splitColumnIndex;

            // Use the header name as-is since we now generate better names in generateNewColumnHeader
            let headerName =
              tableData.headers[colIndex] || `Column ${colIndex + 1}`;

            return (
              <Box
                key={colIndex}
                sx={{
                  flex: 1,
                  p: 1,
                  fontWeight: "bold",
                  borderRight: `1px solid ${borderColor}`,
                  textAlign: "center",
                  fontSize: DIMENSIONS.FONT_SIZE_NORMAL,
                  backgroundColor: isNewColumn
                    ? COLORS.NEW_COLUMN
                    : isOriginalInSplit
                    ? COLORS.MODIFIED_COLUMN
                    : !isAfter
                    ? COLORS.TO_BE_SPLIT
                    : "inherit",
                }}
              >
                {headerName}
                {isNewColumn && (
                  <Typography
                    variant={"caption"}
                    sx={{
                      display: "block",
                      color: "#155724",
                      fontWeight: "bold",
                    }}
                  >
                    {UI_TEXT.NEW}
                  </Typography>
                )}
                {isOriginalInSplit && (
                  <Typography
                    variant={"caption"}
                    sx={{
                      display: "block",
                      color: "#856404",
                      fontWeight: "bold",
                    }}
                  >
                    {UI_TEXT.SPLIT}
                  </Typography>
                )}
                {!isAfter && (
                  <Typography
                    variant={"caption"}
                    sx={{
                      display: "block",
                      color: "#0066cc",
                      fontWeight: "bold",
                    }}
                  >
                    {UI_TEXT.TO_BE_SPLIT}
                  </Typography>
                )}
                <Typography
                  variant={"caption"}
                  color={"textSecondary"}
                  sx={{ display: "block" }}
                >
                  {"(Col "}
                  {colIndex + 1}
                  {")"}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Rows */}
        <Box sx={{ maxHeight: DIMENSIONS.MAX_HEIGHT, overflow: "auto" }}>
          {tableData.rows.map((row, rowIndex) => (
            <Box
              key={rowIndex}
              sx={{
                display: "flex",
                borderBottom: `1px solid ${COLORS.ROW_BORDER}`,
              }}
            >
              {columnsToShow.map((colIndex) => {
                // New column detection: for splits, the new column is at splitColumnIndex + 1
                const isNewColumn =
                  isAfter &&
                  splitColumnIndex !== undefined &&
                  colIndex === splitColumnIndex + 1;
                const isOriginalInSplit =
                  isAfter &&
                  splitColumnIndex !== undefined &&
                  colIndex === splitColumnIndex;
                const cellContent = row[colIndex] || "";

                return (
                  <Box
                    key={`${rowIndex}-${colIndex}`}
                    sx={{
                      flex: 1,
                      p: 1,
                      borderRight: `1px solid ${borderColor}`,
                      fontSize: DIMENSIONS.FONT_SIZE_NORMAL,
                      minHeight: "40px",
                      backgroundColor: isNewColumn
                        ? COLORS.NEW_COLUMN
                        : isOriginalInSplit
                        ? COLORS.MODIFIED_COLUMN
                        : !isAfter
                        ? COLORS.TO_BE_SPLIT
                        : "inherit",
                    }}
                  >
                    {cellContent || (
                      <span style={{ color: "#999", fontStyle: "italic" }}>
                        {UI_TEXT.EMPTY}
                      </span>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Renders a table preview for the confirmation modal
 */
export const renderTablePreview = (
  tableData,
  title,
  isAfter = false,
  scrollRef = null,
  onScroll = null,
  pendingGridLineChanges,
  selectedTable,
  pdfPageDimensions,
  currentPage,
  handleCellDetailClick,
  modalState,
  borderColor
) => {
  // Use the stored splitColumnIndex from the split result if available
  const splitColumnIndex =
    pendingGridLineChanges?.newTableData?.splitColumnIndex;

  if (!tableData || !tableData.headers || !tableData.rows) {
    return (
      <Typography variant={"body2"} color={"text.secondary"}>
        {UI_TEXT.NO_TABLE_DATA}
      </Typography>
    );
  }

  // Detect if this is a row change for proper labeling
  let isRowChange = false;

  if (pendingGridLineChanges) {
    const originalGridLines =
      pendingGridLineChanges.originalGridLines?.[currentPage];
    const newGridLines = pendingGridLineChanges.newGridLines?.[currentPage];

    if (originalGridLines && newGridLines) {
      const originalHorizontalCount = originalGridLines.horizontal?.length || 0;
      const newHorizontalCount = newGridLines.horizontal?.length || 0;

      isRowChange = newHorizontalCount > originalHorizontalCount;
    }
  }

  // Get differences for highlighting
  const differences = pendingGridLineChanges
    ? getCellDifferences(
        pendingGridLineChanges.originalTableData,
        pendingGridLineChanges.newTableData,
        pendingGridLineChanges,
        selectedTable,
        pdfPageDimensions
      )
    : { changes: new Set(), cellMap: {}, changedColumns: new Set() };

  // Determine if this is a structural change (new columns/rows added) vs content change (columns/rows moved)
  const originalColumnCount =
    pendingGridLineChanges?.originalTableData?.headers?.length || 0;
  const newColumnCount =
    pendingGridLineChanges?.newTableData?.headers?.length || 0;
  const originalRowCount =
    pendingGridLineChanges?.originalTableData?.rows?.length || 0;
  const newRowCount = pendingGridLineChanges?.newTableData?.rows?.length || 0;
  const isColumnStructuralChange = newColumnCount !== originalColumnCount;
  const isRowStructuralChange = newRowCount !== originalRowCount;

  // Determine which columns to show
  const columnsToShow = determineColumnsToShow(
    splitColumnIndex,
    isAfter,
    isColumnStructuralChange,
    differences
  );

  // Determine which rows to show
  const rowsToShow = determineRowsToShow(
    isRowStructuralChange,
    isAfter,
    pendingGridLineChanges,
    differences
  );

  // Determine if this is a multi-page column addition
  const isColumnAddition =
    pendingGridLineChanges &&
    (pendingGridLineChanges.originalTableData?.headers?.length || 0) <
      (pendingGridLineChanges.newTableData?.headers?.length || 0);
  const shouldShowAllPages = isColumnAddition && COLUMN_ADDITION_MULTI_PAGE;

  // Filter rows based on page and rowsToShow
  const filteredRows = tableData.rows.filter((row, rowIndex) => {
    if (!rowsToShow || !rowsToShow.includes(rowIndex)) {
      return false;
    }

    // If this is a multi-page column addition, show rows from ALL pages
    if (shouldShowAllPages) {
      return true;
    }

    // For row structural changes (additions), skip page filtering since new rows might not have page info yet
    if (isRowStructuralChange) {
      return true;
    }

    // Otherwise, only show rows from the current page
    // Look for any cell in this row that has page information
    let foundPageInfo = false;
    let rowPage = null;

    for (
      let colIndex = 0;
      colIndex < (tableData.headers?.length || 0);
      colIndex++
    ) {
      // For server-side tables, add +1 to map display row index to server row index
      const serverRowIndex = rowIndex + 1;
      const cellKey = `${serverRowIndex}_${colIndex}`;
      const cellData = selectedTable?.[cellKey];

      if (cellData && cellData[0] && cellData[0].page !== undefined) {
        foundPageInfo = true;
        rowPage = cellData[0].page;
        break;
      }
    }

    // Include only if found page info AND matches current page
    return foundPageInfo && rowPage === currentPage;
  });

  if (!columnsToShow || columnsToShow.length === 0) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography variant={"subtitle2"} sx={{ mb: 1, fontWeight: "bold" }}>
          {title}
        </Typography>
        <Typography variant={"body2"} color={"text.secondary"}>
          {UI_TEXT.NO_CHANGES}
        </Typography>
      </Box>
    );
  }

  if (!filteredRows || filteredRows.length === 0) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography variant={"subtitle2"} sx={{ mb: 1, fontWeight: "bold" }}>
          {title}
        </Typography>
        <Typography variant={"body2"} color={"text.secondary"}>
          {UI_TEXT.NO_ROWS}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant={"subtitle2"} sx={{ mb: 1, fontWeight: "bold" }}>
        {title}{" "}
        {isRowChange
          ? `(${filteredRows.length} rows with changes)`
          : `(${columnsToShow.length} columns with changes)`}
      </Typography>
      <Box
        ref={scrollRef}
        onScroll={onScroll}
        sx={{
          border: "1px solid",
          borderColor: "grey.300",
          borderRadius: 1,
          height: DIMENSIONS.TABLE_HEIGHT,
          overflow: "auto",
          backgroundColor: "background.paper",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: DIMENSIONS.FONT_SIZE_TABLE,
            tableLayout: "auto",
            minWidth: `${columnsToShow.length * 200}px`,
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: COLORS.HEADER_BG,
                position: "sticky",
                top: 0,
                zIndex: 10,
              }}
            >
              {columnsToShow.map((colIndex) => (
                <th
                  key={colIndex}
                  style={{
                    border: `1px solid ${borderColor}`,
                    padding: DIMENSIONS.CELL_PADDING,
                    textAlign: "left",
                    fontWeight: "bold",
                    fontSize: DIMENSIONS.FONT_SIZE_TABLE,
                    width: `${100 / columnsToShow.length}%`,
                    minWidth: DIMENSIONS.MIN_COL_WIDTH,
                    maxWidth: DIMENSIONS.MAX_COL_WIDTH,
                    wordWrap: "break-word",
                    whiteSpace: "normal",
                    verticalAlign: "top",
                  }}
                  title={tableData.headers[colIndex] || `Col ${colIndex + 1}`}
                >
                  {tableData.headers[colIndex] || `Col ${colIndex + 1}`}
                  <br />
                  <span
                    style={{
                      fontSize: DIMENSIONS.FONT_SIZE_TINY,
                      color: "#666",
                      fontWeight: "normal",
                    }}
                  >
                    {`(Col ${colIndex + 1})`}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row, filteredRowIndex) => {
              // Get the original row index for change detection
              const originalRowIndex = tableData.rows.findIndex(
                (originalRow) => originalRow === row
              );

              return (
                <tr key={originalRowIndex}>
                  {columnsToShow.map((colIndex) => {
                    const cellContent = row[colIndex] || "";
                    const cellKey = `${originalRowIndex}_${colIndex}`;
                    const hasChange =
                      differences.changes && differences.changes.has
                        ? differences.changes.has(cellKey)
                        : false;
                    const changeInfo = differences.cellMap[cellKey];

                    // Calculate background color using utility function
                    const backgroundColor = calculateCellBackgroundColor(
                      cellContent,
                      splitColumnIndex,
                      colIndex,
                      isAfter,
                      hasChange,
                      changeInfo
                    );

                    return (
                      <td
                        key={colIndex}
                        style={{
                          border: `1px solid ${borderColor}`,
                          padding: DIMENSIONS.CELL_PADDING,
                          fontSize: DIMENSIONS.FONT_SIZE_SMALL,
                          width: `${100 / columnsToShow.length}%`,
                          minWidth: DIMENSIONS.MIN_COL_WIDTH,
                          maxWidth: DIMENSIONS.MAX_COL_WIDTH,
                          wordWrap: "break-word",
                          whiteSpace: "normal",
                          verticalAlign: "top",
                          backgroundColor,
                          position: "relative",
                          cursor: "pointer",
                          lineHeight: "1.4",
                          transition: "background-color 0.2s ease",
                        }}
                        title={
                          hasChange && changeInfo && changeInfo.type
                            ? `Click to view details. ${changeInfo.type.toUpperCase()}: "${
                                changeInfo.original
                              }" → "${changeInfo.new}"`
                            : `Click to view details. ${cellContent || "Empty"}`
                        }
                        onClick={() =>
                          handleCellDetailClick(
                            originalRowIndex,
                            colIndex,
                            cellContent,
                            tableData,
                            isAfter,
                            {
                              pendingGridLineChanges,
                              selectedTable,
                              pdfPageDimensions,
                              modalState,
                            }
                          )
                        }
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = calculateHoverColor(
                            hasChange,
                            changeInfo
                          );
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = backgroundColor;
                        }}
                      >
                        {cellContent || (
                          <span style={{ color: "#999", fontStyle: "italic" }}>
                            {UI_TEXT.EMPTY}
                          </span>
                        )}
                        {hasChange && changeInfo && changeInfo.type && (
                          <span
                            style={{
                              position: "absolute",
                              top: "6px",
                              right: "8px",
                              fontSize: "1rem",
                              color:
                                changeInfo.type === "added"
                                  ? "#2e7d32"
                                  : changeInfo.type === "removed"
                                  ? "#d32f2f"
                                  : "#1976d2",
                              fontWeight: "bold",
                              textShadow: "0 0 3px rgba(255,255,255,0.9)",
                              zIndex: 1,
                            }}
                          >
                            {"●"}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
      <Typography variant={"caption"} color={"text.secondary"} sx={{ mt: 1 }}>
        {isRowChange
          ? `Showing ${filteredRows.length} of ${tableData.rows.length} rows with changes • ${columnsToShow.length} of ${tableData.headers.length} columns (only changed columns)`
          : `Showing ${columnsToShow.length} of ${tableData.headers.length} columns with changes • ${filteredRows.length} of ${tableData.rows.length} rows (only changed rows)`}
        {differences.changes && differences.changes.size > 0 && (
          <span style={{ marginLeft: "8px", color: "#ff9800" }}>
            {` • ${differences.changes.size} cells changed`}
          </span>
        )}
      </Typography>
    </Box>
  );
};
